/**
 * Sidebar v2 — ZYN-70 Design System TOTVS
 * Grupos por jornada: MEU DIA · PIPELINE · INTELIGÊNCIA · ADMINISTRAÇÃO
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router';
import {
  BarChart3, Users, ChevronRight, ChevronDown, ChevronUp, ChevronLeft, LogOut,
  Building2, Users2, FileUp, FileSpreadsheet, RefreshCw,
  Settings, TrendingUp, TrendingDown, ClipboardList, Bell,
  ScrollText, X, BookOpen, Zap, GitMerge, Webhook,
  Sliders, Flag, CreditCard, ArrowUpRight, ListChecks, Search, Plus, Keyboard,
  Funnel, Tag, Key,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { useMyProgress } from '../hooks/useMyProgress';
import { useMenuConfig } from '../hooks/useMenuConfig';
import { api } from '../services/api';
import TotvsLogo from './TotvsLogo';
import PortfolioTransferDrawer from './PortfolioTransferDrawer';

// ─── Cores por grupo de navegação ──────────────────────────────────────────────
const GROUP_COLORS = {
  dia:      '#00DBFF',
  pipeline: '#00FFD4',
  intel:    '#A44DFF',
  admin:    '#94a3b8',
};

// ─── Componentes internos ───────────────────────────────────────────────────────

function GroupLabel({ children, color, roleTag, collapsed }) {
  // ZYN-328: no modo reduzido vira só uma linha separadora fina, sem texto
  if (collapsed) {
    return <div className="h-px mx-3 mt-4 mb-1 opacity-20" style={{ backgroundColor: color }} />;
  }
  return (
    <div className="flex items-center gap-2 px-4 mt-4 mb-1">
      <div className="h-px flex-1 opacity-20" style={{ backgroundColor: color }} />
      <span
        className="text-[9px] font-bold tracking-[0.18em] uppercase"
        style={{ color }}
      >
        {children}
      </span>
      {roleTag && (
        <span className="text-[8px] font-semibold px-1 py-0.5 rounded opacity-60"
          style={{ backgroundColor: `${color}22`, color }}>
          {roleTag}
        </span>
      )}
      <div className="h-px flex-1 opacity-20" style={{ backgroundColor: color }} />
    </div>
  );
}

function NavItem({ path, label, icon: Icon, badge, badgeText, groupColor, dataTour, exact, collapsed }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isActive = exact ? location.pathname === path : location.pathname.startsWith(path);
  const color = groupColor || '#00DBFF';
  const hasBadge = badge != null && badge > 0;

  if (collapsed) {
    return (
      <button
        onClick={() => navigate(path)}
        data-tour={dataTour}
        title={label}
        className="w-full flex items-center justify-center py-2.5 relative transition-all"
        style={{
          backgroundColor: isActive ? `${color}15` : 'transparent',
          borderLeft: isActive ? `2px solid ${color}` : '2px solid transparent',
        }}
        onMouseEnter={e => { if (!isActive) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.04)'; }}
        onMouseLeave={e => { if (!isActive) e.currentTarget.style.backgroundColor = 'transparent'; }}
      >
        <Icon size={17} style={{ color: isActive ? color : 'rgba(255,255,255,0.70)' }} />
        {hasBadge && (
          <span className="absolute top-1 right-2.5 min-w-[14px] h-[14px] flex items-center justify-center rounded-full bg-red-500 text-[8px] font-bold text-white px-0.5">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </button>
    );
  }

  return (
    <button
      onClick={() => navigate(path)}
      data-tour={dataTour}
      className="w-full flex items-center gap-3 px-4 py-2 text-sm transition-all relative"
      style={{
        color: isActive ? '#ffffff' : 'rgba(255,255,255,0.70)',
        backgroundColor: isActive ? `${color}15` : 'transparent',
        borderLeft: isActive ? `2px solid ${color}` : '2px solid transparent',
      }}
      onMouseEnter={e => { if (!isActive) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.04)'; }}
      onMouseLeave={e => { if (!isActive) e.currentTarget.style.backgroundColor = 'transparent'; }}
    >
      <Icon size={16} className="shrink-0" style={{ color: isActive ? color : undefined }} />
      <span className="flex-1 text-left text-[13px]">{label}</span>
      {hasBadge && (
        <span className="min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white px-1">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
      {badgeText && (
        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded"
          style={{ backgroundColor: '#D9FF6522', color: '#D9FF65' }}>
          {badgeText}
        </span>
      )}
      {isActive && !badge && !badgeText && (
        <ChevronRight size={11} style={{ color }} />
      )}
    </button>
  );
}

function SubNavItem({ path, label, icon: Icon, groupColor }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isActive = location.pathname === path;
  const color = groupColor || GROUP_COLORS.pipeline;
  return (
    <button
      onClick={() => navigate(path)}
      className="w-full flex items-center gap-2.5 pl-8 pr-4 py-1.5 text-[12px] transition-colors"
      style={{ color: isActive ? '#ffffff' : 'rgba(255,255,255,0.55)' }}
      onMouseEnter={e => { if (!isActive) e.currentTarget.style.color = 'rgba(255,255,255,0.85)'; }}
      onMouseLeave={e => { if (!isActive) e.currentTarget.style.color = 'rgba(255,255,255,0.55)'; }}
    >
      {/* Indicador de ativo */}
      <span className="w-1.5 h-1.5 rounded-full shrink-0 transition-colors"
        style={{ backgroundColor: isActive ? color : 'rgba(255,255,255,0.18)' }} />
      {Icon && <Icon size={12} className="shrink-0" />}
      <span className="flex-1 text-left font-medium">{label}</span>
    </button>
  );
}

// Reusa as 3 seções (Operação/Dados/Sistema) do accordion de Configurações —
// só aparece quando effectiveCollapsed é false (expandido ou hover-expand).
function ConfigFlyoutSections({ operacao, dados, sistema }) {
  return (
    <>
      {operacao.length > 0 && (
        <>
          <p className="pl-4 pt-1.5 pb-0.5 text-[9px] font-bold tracking-wider uppercase"
            style={{ color: 'rgba(255,255,255,0.30)' }}>Operação</p>
          {operacao.map(item => (
            <SubNavItem key={item.path} path={item.path} label={item.label} icon={item.icon} />
          ))}
        </>
      )}
      {dados.length > 0 && (
        <>
          <p className="pl-4 pt-1.5 pb-0.5 text-[9px] font-bold tracking-wider uppercase"
            style={{ color: 'rgba(255,255,255,0.30)' }}>Dados</p>
          {dados.map(item => (
            <SubNavItem key={item.path} path={item.path} label={item.label} icon={item.icon} />
          ))}
        </>
      )}
      {sistema.length > 0 && (
        <>
          <p className="pl-4 pt-1.5 pb-0.5 text-[9px] font-bold tracking-wider uppercase"
            style={{ color: 'rgba(255,255,255,0.30)' }}>Sistema</p>
          {sistema.map(item => (
            <SubNavItem key={item.path} path={item.path} label={item.label} icon={item.icon} />
          ))}
        </>
      )}
    </>
  );
}

// ─── FunnelNavGroup — accordion New Names / Base ───────────────────────────
// No modo reduzido (effectiveCollapsed), vira um ícone simples que navega
// direto — o hover-expand do rail inteiro (ZYN-329) já cuida de mostrar o
// accordion com sub-itens, sem precisar de flyout próprio.

function FunnelNavGroup({ canAccessFunnel, collapsed }) {
  const location = useLocation();
  const navigate = useNavigate();
  const color = GROUP_COLORS.pipeline;

  const isOnFunnel = location.pathname === '/new-names' || location.pathname === '/base';
  const [open, setOpen] = useState(isOnFunnel);

  // Auto-abre quando o usuário entra em qualquer rota de funil
  useEffect(() => {
    if (isOnFunnel) setOpen(true);
  }, [isOnFunnel]);

  function handleToggle() {
    const next = !open;
    setOpen(next);
    // Ao abrir, navega para o primeiro funil acessível
    if (next && !isOnFunnel) {
      navigate(canAccessFunnel('new_names') ? '/new-names' : '/base');
    }
  }

  if (collapsed) {
    return (
      <button
        data-tour="sidebar-funnel"
        onClick={() => navigate(canAccessFunnel('new_names') ? '/new-names' : '/base')}
        title="Funil de Vendas"
        className="w-full flex items-center justify-center py-2.5 relative transition-all"
        style={{
          backgroundColor: isOnFunnel ? `${color}15` : 'transparent',
          borderLeft: isOnFunnel ? `2px solid ${color}` : '2px solid transparent',
        }}
      >
        <BarChart3 size={17} style={{ color: isOnFunnel ? color : 'rgba(255,255,255,0.70)' }} />
      </button>
    );
  }

  return (
    <>
      {/* Cabeçalho do accordion */}
      <button
        data-tour="sidebar-funnel"
        onClick={handleToggle}
        className="w-full flex items-center gap-3 px-4 py-2 text-sm transition-all relative"
        style={{
          color: isOnFunnel ? '#ffffff' : 'rgba(255,255,255,0.70)',
          backgroundColor: isOnFunnel ? `${color}15` : 'transparent',
          borderLeft: isOnFunnel ? `2px solid ${color}` : '2px solid transparent',
        }}
        onMouseEnter={e => { if (!isOnFunnel) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.04)'; }}
        onMouseLeave={e => { if (!isOnFunnel) e.currentTarget.style.backgroundColor = 'transparent'; }}
      >
        <BarChart3 size={16} className="shrink-0" style={{ color: isOnFunnel ? color : undefined }} />
        <span className="flex-1 text-left text-[13px]">Funil de Vendas</span>
        {open
          ? <ChevronDown size={12} style={{ color: isOnFunnel ? color : 'rgba(255,255,255,0.35)' }} />
          : <ChevronRight size={12} style={{ color: 'rgba(255,255,255,0.35)' }} />}
      </button>

      {/* Sub-itens */}
      {open && (
        <div>
          {canAccessFunnel('new_names') && (
            <SubNavItem path="/new-names" label="New Names" />
          )}
          {canAccessFunnel('base') && (
            <SubNavItem path="/base" label="Base" />
          )}
        </div>
      )}
    </>
  );
}

// ─── RoleBadge ─────────────────────────────────────────────────────────────────
function RoleBadge({ role }) {
  const cfg = {
    admin:   { label: 'Admin',   bg: '#A44DFF22', color: '#A44DFF' },
    manager: { label: 'Gerente', bg: '#00DBFF22', color: '#00DBFF' },
    esn:     { label: 'ESN',     bg: '#00FFD422', color: '#00FFD4' },
    apn:     { label: 'APN',     bg: '#FF890022', color: '#FF8900' },
  }[role] || { label: role, bg: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)' };
  return (
    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide"
      style={{ backgroundColor: cfg.bg, color: cfg.color }}>
      {cfg.label}
    </span>
  );
}

// ─── Avatar com gradiente ───────────────────────────────────────────────────────
function UserAvatar({ initial }) {
  return (
    <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
      style={{ background: 'linear-gradient(135deg, #00DBFF 0%, #A44DFF 100%)' }}>
      <span className="text-xs font-bold text-white uppercase">{initial ?? '?'}</span>
    </div>
  );
}

// ─── Sidebar principal ──────────────────────────────────────────────────────────
export default function Sidebar({ onMobileClose, onPaletteOpen, onNewLead, collapsed = false, onToggleCollapsed }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [configOpen, setConfigOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [reconciliacaoPendente, setReconciliacaoPendente] = useState(0);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [orphanCount, setOrphanCount] = useState(0);
  const [drawerOpen, setDrawerOpen]   = useState(false);
  const userMenuRef = useRef(null);

  // ZYN-329: hover-expand — o rail reduzido expande por cima do conteúdo ao
  // passar o mouse, reaproveitando o mesmo JSX do modo expandido (sem flyouts
  // por item). `collapsed` continua sendo o estado fixo/pinado; `hovering` é
  // só uma sobreposição temporária por cima dele.
  const [hovering, setHovering] = useState(false);
  const hoverCloseTimeout = useRef(null);
  const effectiveCollapsed = collapsed && !hovering;

  useEffect(() => () => clearTimeout(hoverCloseTimeout.current), []);

  function handleRailMouseEnter() {
    if (!collapsed) return;
    clearTimeout(hoverCloseTimeout.current);
    setHovering(true);
  }
  function handleRailMouseLeave() {
    if (!collapsed) return;
    hoverCloseTimeout.current = setTimeout(() => setHovering(false), 120);
  }

  const role = user?.role;
  const funnelAccess = user?.funnel_access || 'both';
  const isAdmin = role === 'admin';
  const isManager = role === 'manager';
  const isEsn = role === 'esn';
  const isApn = role === 'apn';
  const isIntel = isAdmin || isManager; // acesso ao grupo Inteligência
  const { enabled: apnEnabled } = useFeatureFlag('apn_workspace');
  const { progress: myProgress } = useMyProgress();
  const { itemsByGroup } = useMenuConfig();

  function canAccessFunnel(funnel) {
    if (!isEsn) return true;
    return funnelAccess === 'both' || funnelAccess === funnel;
  }

  // ZYN-326: itens do registry visíveis para o papel atual, num grupo
  function visibleItems(groupKey) {
    return (itemsByGroup[groupKey] || []).filter(it => it.visible && role && it.roles.includes(role));
  }

  // Mapeia badgeSource declarado no registry para o valor ao vivo correspondente
  function badgeValueFor(source) {
    if (source === 'unread') return unreadCount;
    if (source === 'reconciliacao') return reconciliacaoPendente;
    return undefined;
  }

  // Renderiza um item do registry — NavItem padrão, ou componente especial
  // quando `custom` está definido (accordion do funil, Equipe c/ badge, etc.)
  function renderRegistryItem(item, groupColor) {
    if (item.custom === 'funnelGroup') {
      return (canAccessFunnel('new_names') || canAccessFunnel('base'))
        ? <FunnelNavGroup key={item.key} canAccessFunnel={canAccessFunnel} collapsed={effectiveCollapsed} />
        : null;
    }
    if (item.custom === 'teamWithBadge') {
      return (
        <div key={item.key} className="relative flex items-center">
          <NavItem path="/team" label={item.label} icon={item.icon} groupColor={groupColor} exact collapsed={effectiveCollapsed} />
          {(isAdmin || isManager) && orphanCount > 0 && (
            <button
              onClick={() => setDrawerOpen(true)}
              title={`${orphanCount} registro(s) sem executivo ativo — clique para redistribuir`}
              className={`absolute flex items-center justify-center w-5 h-5 rounded-full bg-amber-500 text-black text-[10px] font-bold hover:bg-amber-400 transition-colors cursor-pointer z-10 ${effectiveCollapsed ? 'top-0.5 right-1.5' : 'right-3'}`}
            >
              {orphanCount > 99 ? '99+' : orphanCount}
            </button>
          )}
        </div>
      );
    }
    if (item.custom === 'configAccordion') {
      return null; // renderizado à parte, depende de configGroup* calculados abaixo
    }
    return (
      <NavItem
        key={item.key}
        path={item.path}
        label={item.label}
        icon={item.icon}
        groupColor={groupColor}
        dataTour={item.dataTour}
        badgeText={item.badgeText}
        badge={badgeValueFor(item.badgeSource)}
        exact
        collapsed={effectiveCollapsed}
      />
    );
  }

  const configPaths = [
    '/metas', '/automations', '/cross-sell', '/playbooks', '/esns-admin', '/users',
    '/import', '/import/realizados', '/import/totvs-email', '/sync', '/audit-log',
    '/duplicates', '/custom-fields', '/webhooks', '/admin/feature-flags', '/billing',
    '/admin/reconciliacao', '/admin/rotting-config', '/admin/required-fields',
    '/admin/lead-origins', '/team',
  ];

  useEffect(() => {
    if (configPaths.includes(location.pathname)) setConfigOpen(true);
  }, [location.pathname]);

  useEffect(() => {
    if (!userMenuOpen) return;
    const handler = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [userMenuOpen]);

  useEffect(() => {
    function fetchCounts() {
      api.getUnreadCount().then(d => setUnreadCount(d.unread || 0)).catch(() => {});
      api.getReconciliacaoPending().then(d => setReconciliacaoPendente(d.count || 0)).catch(() => {});
    }
    fetchCounts();
    const interval = setInterval(fetchCounts, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!isAdmin && !isManager) return;
    api.getOrphanRecords(false)
      .then(data => setOrphanCount(data?.total ?? 0))
      .catch(() => {});
  }, [isAdmin, isManager]);

  const handleTransferred = useCallback(() => {
    api.getOrphanRecords(false)
      .then(data => setOrphanCount(data?.total ?? 0))
      .catch(() => {});
  }, []);

  function handleLogout() { logout(); navigate('/login'); }

  // ── Groups das configurações (admin expandido) ────────────────────────────
  const configGroupOperacao = [
    { path: '/metas',       label: 'Planejamento',  icon: ClipboardList, show: isIntel },
    { path: '/automations', label: 'Automações',    icon: Zap,           show: isIntel },
    { path: '/cross-sell',  label: 'Cross-sell',    icon: ArrowUpRight,  show: !isApn },
    { path: '/playbooks',   label: 'Playbooks',     icon: ListChecks,    show: !isApn },
  ].filter(i => i.show);

  const configGroupDados = [
    { path: '/import',              label: 'Importação',          icon: FileUp,          show: isIntel },
    { path: '/import/realizados',   label: 'Importar Realizados', icon: FileSpreadsheet, show: isIntel },
    { path: '/sync',                label: 'Integração ERP',      icon: RefreshCw,       show: isIntel },
    { path: '/duplicates',          label: 'Duplicatas',          icon: GitMerge,        show: isIntel },
  ].filter(i => i.show);

  const configGroupSistema = [
    { path: '/admin/notifications',   label: 'Notificações',       icon: Bell,        show: isIntel },
    { path: '/admin/rotting-config',  label: 'Deal Rotting',       icon: TrendingDown, show: isIntel },
    { path: '/admin/required-fields', label: 'Campos Obrigatórios',icon: ListChecks,  show: isIntel },
    { path: '/admin/lead-origins',    label: 'Origens de Lead',    icon: Tag,         show: isIntel },
    { path: '/custom-fields',         label: 'Campos Custom',      icon: Sliders,     show: isAdmin },
    { path: '/webhooks',              label: 'Webhooks',           icon: Webhook,     show: isAdmin },
    { path: '/audit-log',             label: 'Log de Atividades',  icon: ScrollText,  show: isAdmin },
    { path: '/admin/feature-flags',   label: 'Feature Flags',      icon: Flag,        show: isAdmin },
    { path: '/admin/api-keys',        label: 'Chaves de API',      icon: Key,         show: isAdmin },
    { path: '/billing',               label: 'Billing & Plano',    icon: CreditCard,  show: isAdmin },
  ].filter(i => i.show);

  const allConfigItems = [...configGroupOperacao, ...configGroupDados, ...configGroupSistema];
  const isConfigActive = allConfigItems.some(i => location.pathname === i.path);

  // ─── Sidebar render ───────────────────────────────────────────────────────
  return (
    <>
    <aside
      onMouseEnter={handleRailMouseEnter}
      onMouseLeave={handleRailMouseLeave}
      className={`flex flex-col overflow-hidden transition-[width] duration-200 ${
        onToggleCollapsed ? 'absolute inset-y-0 left-0 z-30' : 'relative h-full'
      } ${effectiveCollapsed ? 'w-16' : 'w-60'}`}
      style={{ backgroundColor: '#001623' }}
    >
      {/* ── Logo ──────────────────────────────────────────────────────── */}
      <div
        className={`h-14 flex items-center border-b shrink-0 ${effectiveCollapsed ? 'justify-center px-2' : 'px-4 gap-3'}`}
        style={{ backgroundColor: '#001020', borderColor: 'rgba(255,255,255,0.08)' }}
      >
        <TotvsLogo height={18} color="white" />
        {!effectiveCollapsed && (
          <span
            className="text-[11px] font-bold tracking-[0.35em] uppercase"
            style={{ color: '#00DBFF' }}
          >
            Moda
          </span>
        )}
        {onMobileClose && (
          <button onClick={onMobileClose} className="ml-auto opacity-40 hover:opacity-80 md:hidden">
            <X size={16} className="text-white" />
          </button>
        )}
      </div>

      {/* ── Busca ─────────────────────────────────────────────────────── */}
      {onPaletteOpen && !effectiveCollapsed && (
        <div className="px-3 pt-3 pb-1">
          <button
            type="button"
            onClick={onPaletteOpen}
            className="w-full px-3 py-2 rounded-lg flex items-center gap-2 text-left transition-all"
            style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.09)' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#00DBFF44'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.09)'; }}
          >
            <Search size={13} className="shrink-0" style={{ color: 'rgba(255,255,255,0.35)' }} />
            <span className="flex-1 text-[12px]" style={{ color: 'rgba(255,255,255,0.35)' }}>Buscar…</span>
            <kbd className="text-[10px] font-mono px-1.5 py-0.5 rounded"
              style={{ backgroundColor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.4)' }}>⌘K</kbd>
          </button>
        </div>
      )}
      {onPaletteOpen && effectiveCollapsed && (
        <div className="px-3 pt-3 pb-1">
          <button
            type="button"
            onClick={onPaletteOpen}
            title="Buscar (⌘K)"
            className="w-full py-2 rounded-lg flex items-center justify-center transition-all"
            style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.09)' }}
          >
            <Search size={14} style={{ color: 'rgba(255,255,255,0.45)' }} />
          </button>
        </div>
      )}

      {/* ── Novo Lead CTA ─────────────────────────────────────────────── */}
      {onNewLead && !effectiveCollapsed && (
        <div className="px-3 pt-2 pb-1">
          <button
            type="button"
            onClick={onNewLead}
            className="w-full py-2 rounded-lg font-bold text-[13px] flex items-center justify-center gap-1.5 transition-colors"
            style={{ backgroundColor: '#00DBFF', color: '#002233' }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#00c8eb'; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#00DBFF'; }}
          >
            <Plus size={14} strokeWidth={2.5} />
            Novo Lead
          </button>
        </div>
      )}
      {onNewLead && effectiveCollapsed && (
        <div className="px-3 pt-2 pb-1">
          <button
            type="button"
            onClick={onNewLead}
            title="Novo Lead"
            className="w-full py-2 rounded-lg flex items-center justify-center transition-colors"
            style={{ backgroundColor: '#00DBFF', color: '#002233' }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#00c8eb'; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#00DBFF'; }}
          >
            <Plus size={16} strokeWidth={2.5} />
          </button>
        </div>
      )}

      {/* ── Navegação ─────────────────────────────────────────────────── */}
      <nav className="flex-1 overflow-y-auto py-1 scrollbar-thin">

        {/* ══ MEU DIA — registry-driven (ZYN-326) ══ */}
        {!isApn && (
          <>
            <GroupLabel color={GROUP_COLORS.dia} collapsed={effectiveCollapsed}>Meu Dia</GroupLabel>
            {visibleItems('dia').map(item => renderRegistryItem(item, GROUP_COLORS.dia))}
          </>
        )}

        {/* ══ PIPELINE — registry-driven (ZYN-326) ══ */}
        {!isApn && (
          <>
            <GroupLabel color={GROUP_COLORS.pipeline} collapsed={effectiveCollapsed}>Pipeline</GroupLabel>
            {visibleItems('pipeline').map(item => renderRegistryItem(item, GROUP_COLORS.pipeline))}
          </>
        )}

        {/* ══ INTELIGÊNCIA (Manager + Admin) — registry-driven (ZYN-326) ══ */}
        {isIntel && !isApn && (
          <>
            <GroupLabel color={GROUP_COLORS.intel} roleTag="GER" collapsed={effectiveCollapsed}>Inteligência</GroupLabel>
            {visibleItems('intel').map(item => renderRegistryItem(item, GROUP_COLORS.intel))}
          </>
        )}

        {/* ══ Metas (ESN vê as próprias) — registry-driven (ZYN-326) ══ */}
        {isEsn && (
          <>
            <GroupLabel color={GROUP_COLORS.intel} collapsed={effectiveCollapsed}>Métricas</GroupLabel>
            {visibleItems('intel').map(item => renderRegistryItem(item, GROUP_COLORS.intel))}
          </>
        )}

        {/* ══ ADMINISTRAÇÃO (Admin) — registry-driven (ZYN-326) ══ */}
        {isAdmin && !isApn && (
          <>
            <GroupLabel color={GROUP_COLORS.admin} roleTag="ADM" collapsed={effectiveCollapsed}>Administração</GroupLabel>
            {visibleItems('admin')
              .filter(item => item.custom !== 'configAccordion')
              .map(item => renderRegistryItem(item, GROUP_COLORS.admin))}

            {/* Configurações expandível — ao passar o mouse no rail reduzido,
                effectiveCollapsed vira false e mostra o mesmo accordion do
                modo expandido, sem lógica de flyout própria (ZYN-329) */}
            {allConfigItems.length > 0 && visibleItems('admin').some(i => i.key === 'config-accordion') && (
              effectiveCollapsed ? (
                <button
                  onClick={() => setConfigOpen(o => !o)}
                  title="Configurações"
                  className="w-full flex items-center justify-center py-2.5 relative transition-all"
                  style={{
                    backgroundColor: isConfigActive ? `${GROUP_COLORS.admin}15` : 'transparent',
                    borderLeft: isConfigActive ? `2px solid ${GROUP_COLORS.admin}` : '2px solid transparent',
                  }}
                >
                  <Settings size={17} style={{ color: isConfigActive ? GROUP_COLORS.admin : 'rgba(255,255,255,0.70)' }} />
                </button>
              ) : (
                <>
                  <button
                    onClick={() => setConfigOpen(o => !o)}
                    className="w-full flex items-center gap-3 px-4 py-2 text-[13px] transition-all"
                    style={{
                      color: isConfigActive ? '#ffffff' : 'rgba(255,255,255,0.60)',
                      borderLeft: isConfigActive ? `2px solid ${GROUP_COLORS.admin}` : '2px solid transparent',
                      backgroundColor: isConfigActive ? `${GROUP_COLORS.admin}15` : 'transparent',
                    }}
                  >
                    <Settings size={16} className="shrink-0" style={{ color: isConfigActive ? GROUP_COLORS.admin : undefined }} />
                    <span className="flex-1 text-left">Configurações</span>
                    {configOpen
                      ? <ChevronDown size={11} style={{ color: 'rgba(255,255,255,0.35)' }} />
                      : <ChevronRight size={11} style={{ color: 'rgba(255,255,255,0.35)' }} />}
                  </button>
                  {configOpen && (
                    <div style={{ borderLeft: `1px solid ${GROUP_COLORS.admin}30`, marginLeft: '16px' }}>
                      <ConfigFlyoutSections
                        operacao={configGroupOperacao}
                        dados={configGroupDados}
                        sistema={configGroupSistema}
                      />
                    </div>
                  )}
                </>
              )
            )}
          </>
        )}

        {/* ══ Manager: badge transferência carteira ══ */}
        {isManager && !isAdmin && !isApn && orphanCount > 0 && (
          <div className="relative flex items-center">
            <NavItem path="/team" label="Equipe & Permissões" icon={Users2} groupColor={GROUP_COLORS.admin} exact collapsed={effectiveCollapsed} />
            <button
              onClick={() => setDrawerOpen(true)}
              title={`${orphanCount} registro(s) sem executivo ativo — clique para redistribuir`}
              className={`absolute flex items-center justify-center w-5 h-5 rounded-full bg-amber-500 text-black text-[10px] font-bold hover:bg-amber-400 transition-colors cursor-pointer z-10 ${effectiveCollapsed ? 'top-0.5 right-1.5' : 'right-3'}`}
            >
              {orphanCount > 99 ? '99+' : orphanCount}
            </button>
          </div>
        )}

        {/* ══ Manager: sub-config ══ */}
        {isManager && !isAdmin && !isApn && configGroupOperacao.length > 0 && (
          <>
            <GroupLabel color={GROUP_COLORS.admin} collapsed={effectiveCollapsed}>Operações</GroupLabel>
            {configGroupOperacao.map(item => (
              <NavItem key={item.path} path={item.path} label={item.label} icon={item.icon} groupColor={GROUP_COLORS.admin} exact collapsed={effectiveCollapsed} />
            ))}
          </>
        )}

        {/* ══ APN WORKSPACE ══ */}
        {apnEnabled && isApn && (
          <>
            <GroupLabel color={GROUP_COLORS.pipeline} collapsed={effectiveCollapsed}>APN Workspace</GroupLabel>
            <NavItem path="/apn/dashboard" label="Meus Prospects" icon={Users2} groupColor={GROUP_COLORS.pipeline} exact collapsed={effectiveCollapsed} />
            <NavItem path="/apn/metrics" label="Minhas Métricas" icon={TrendingUp} groupColor={GROUP_COLORS.pipeline} exact collapsed={effectiveCollapsed} />
          </>
        )}
      </nav>

      {/* ── Barra de meta (ZYN-53) ─────────────────────────────────── */}
      {myProgress.length > 0 && !effectiveCollapsed && (
        <div className="px-3 py-2.5 border-t shrink-0" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
          {myProgress.map(p => {
            const pct = p.pct_saas ?? 0;
            const barColor = pct >= 100 ? '#00FFD4' : pct >= 80 ? '#00DBFF' : pct >= 50 ? '#D9FF65' : '#FF8900';
            const funLabel = p.funnel_type === 'new_names' ? 'Novos' : 'Base';
            return (
              <div key={p.funnel_type} className="mb-1.5 last:mb-0">
                <div className="flex justify-between text-[9px] mb-0.5" style={{ color: 'rgba(255,255,255,0.40)' }}>
                  {myProgress.length > 1 && <span>{funLabel}</span>}
                  {myProgress.length === 1 && <span>Meta SaaS</span>}
                  <span style={{ color: barColor }}>{pct}%</span>
                </div>
                <div className="w-full h-1 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.10)' }}>
                  <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, pct)}%`, backgroundColor: barColor }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Toggle modo reduzido (ZYN-328/329) ──────────────────────────
          O título/ícone refletem o estado fixo (`collapsed`, o que vai
          acontecer ao clicar); o layout (padding/label) reflete o que está
          visualmente na tela agora (`effectiveCollapsed`, que inclui o
          hover-expand temporário). */}
      {onToggleCollapsed && (
        <button
          type="button"
          onClick={onToggleCollapsed}
          title={collapsed ? 'Fixar menu expandido ([)' : 'Recolher menu ([)'}
          className={`shrink-0 flex items-center gap-2 py-2 text-[11px] transition-colors border-t ${effectiveCollapsed ? 'justify-center' : 'px-4'}`}
          style={{ color: 'rgba(255,255,255,0.40)', borderColor: 'rgba(255,255,255,0.08)' }}
          onMouseEnter={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.75)'; }}
          onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.40)'; }}
        >
          {collapsed ? <ChevronRight size={15} /> : <ChevronLeft size={15} />}
          {!effectiveCollapsed && <span>{collapsed ? 'Fixar expandido' : 'Recolher menu'}</span>}
        </button>
      )}

      {/* ── Footer: usuário ───────────────────────────────────────────── */}
      <div
        className="border-t relative shrink-0"
        style={{ backgroundColor: '#001020', borderColor: 'rgba(255,255,255,0.08)' }}
        ref={userMenuRef}
      >
        {/* Dropdown menu */}
        {userMenuOpen && (
          <div
            className={`absolute bottom-full mb-2 rounded-xl overflow-hidden shadow-2xl border ${effectiveCollapsed ? 'left-2 w-52' : 'left-2 right-2'}`}
            style={{ backgroundColor: '#011a28', borderColor: 'rgba(255,255,255,0.10)' }}
          >
            <button type="button" onClick={() => { setUserMenuOpen(false); navigate('/perfil'); }}
              className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-[12px] text-left transition-colors"
              style={{ color: 'rgba(255,255,255,0.70)' }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}>
              <Settings size={13} />
              <span className="flex-1">Meu Perfil</span>
            </button>
            <a href="/manual-totvs-crm.html" target="_blank" rel="noopener noreferrer"
              onClick={() => setUserMenuOpen(false)}
              className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-[12px] transition-colors"
              style={{ color: 'rgba(255,255,255,0.70)' }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}>
              <BookOpen size={13} />
              <span className="flex-1">Manual de Operação</span>
              <span className="text-[9px] px-1.5 py-0.5 rounded font-medium"
                style={{ backgroundColor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.40)' }}>PDF</span>
            </a>
            <div className="border-t" style={{ borderColor: 'rgba(255,255,255,0.08)' }} />
            <button type="button" onClick={handleLogout}
              className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-[12px] text-left transition-colors"
              style={{ color: '#FF009E' }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#FF009E10'; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}>
              <LogOut size={13} />
              <span className="flex-1">Sair da conta</span>
            </button>
          </div>
        )}

        {effectiveCollapsed ? (
          <button type="button" onClick={() => setUserMenuOpen(o => !o)}
            title={user?.username}
            className="w-full flex items-center justify-center py-3 transition-colors"
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.04)'; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}>
            <UserAvatar initial={user?.username?.[0]} />
          </button>
        ) : (
          <button type="button" onClick={() => setUserMenuOpen(o => !o)}
            className="w-full flex items-center gap-2.5 px-4 py-3 transition-colors"
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.04)'; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}>
            <UserAvatar initial={user?.username?.[0]} />
            <div className="min-w-0 flex-1 text-left">
              <p className="text-[12px] font-semibold truncate" style={{ color: 'rgba(255,255,255,0.90)' }}>
                {user?.username}
              </p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <RoleBadge role={user?.role} />
                {user?.esn_nome && (
                  <span className="text-[9px] truncate" style={{ color: 'rgba(255,255,255,0.40)' }}>
                    {user.esn_nome}
                  </span>
                )}
              </div>
            </div>
            {userMenuOpen
              ? <ChevronDown size={13} style={{ color: 'rgba(255,255,255,0.35)' }} />
              : <ChevronUp size={13} style={{ color: 'rgba(255,255,255,0.35)' }} />}
          </button>
        )}
      </div>
    </aside>
    {drawerOpen && (isAdmin || isManager) && (
      <PortfolioTransferDrawer
        onClose={() => setDrawerOpen(false)}
        onTransferred={handleTransferred}
      />
    )}
    </>
  );
}
